import React from 'react';
import { NavLink } from 'react-router-dom';
import { Menu, OverlayTrigger, Popover } from '@econ/ui-elements';
import debounce from 'lodash.debounce';
import icons from './icons';
import Authorize from '../../shared/components/Authorize';
import { withEContext, useEContext } from '../../shared/components/EContext';
import Text, { text } from '../../shared/components/l10n/TextByRoute';
import TextById from '../../shared/components/l10n/Text';
import MenuLink from '../../shared/components/AuthorizedMenuLink';

const LINK_WIDTH = 82;

interface Props extends React.HTMLProps<any> {
    autoCollapse?: boolean;
    children: any;
    context?: any;
}

interface State {
    authorizedRoutes: any;
    boundaryRoute: any;
}

function getAuthorizedRoutes(props) {
    const availableRoutes = Object.keys(props.context.routes);
    const linkRoutes = props.children.reduce((routes, link) => {
        if (link.props && link.props.route) routes.push(link.props.route);
        if (Array.isArray(link)) routes.push(...link.filter(l => !!l.props && !!l.props.route).map(l => l.props.route));
        return routes;
    }, []);
    return linkRoutes.filter(route => availableRoutes.includes(route.split('?')[0]));
}

class Navigation extends React.Component<Props, State> {
    state = {
        authorizedRoutes: getAuthorizedRoutes(this.props),
        boundaryRoute: undefined,
    };

    // refs
    navigation: React.RefObject<any> = React.createRef();
    list: React.RefObject<any> = React.createRef();

    componentDidMount() {
        if (this.props.autoCollapse) {
            this.setBoundaryRoute();
            window.addEventListener('resize', this.setAvailableWidth);
        }
    }

    componentWillUnmount() {
        if (this.props.autoCollapse) {
            window.removeEventListener('resize', this.setAvailableWidth);
        }
    }

    render() {
        const { autoCollapse, children, context, ...props } = this.props;
        let links = children;

        if (autoCollapse) {
            const { boundaryRoute } = this.state;

            if (boundaryRoute) {
                const visibleLinks = React.Children.toArray(children);
                const hiddenLinks = visibleLinks.splice(
                    visibleLinks.findIndex((link: any) => link.props.route === boundaryRoute)
                );

                links = (
                    <React.Fragment>
                        {visibleLinks}
                        <li>
                            <OverlayTrigger
                                overlay={
                                    <Popover width="175px">
                                        {({ closePopover }) => (
                                            <Menu>
                                                {hiddenLinks.map((link: any) =>
                                                    link.props.route ? (
                                                        <MenuLink
                                                            key={link.props.route}
                                                            href={link.props.route}
                                                            onClick={closePopover}>
                                                            {link.props.children}
                                                        </MenuLink>
                                                    ) : null
                                                )}
                                            </Menu>
                                        )}
                                    </Popover>
                                }
                                position="bottom">
                                {({ toggleOverlay }) => (
                                    <button onClick={toggleOverlay}>
                                        {icons.more}
                                        <span>
                                            <TextById id="TOP_NAVIGATION_MORE_TEXT">More</TextById>
                                        </span>
                                    </button>
                                )}
                            </OverlayTrigger>
                        </li>
                    </React.Fragment>
                );
            }
        }

        return (
            <nav {...props} ref={this.navigation}>
                <ul ref={this.list}>{links}</ul>
            </nav>
        );
    }

    setAvailableWidth = debounce(() => this.setBoundaryRoute(), 100);

    setBoundaryRoute = () => {
        this.setState(state => {
            const boundaryRouteIndex = Math.floor(this.navigation.current.offsetWidth / LINK_WIDTH);
            return {
                boundaryRoute:
                    boundaryRouteIndex >= state.authorizedRoutes.length ? null : state.authorizedRoutes[boundaryRouteIndex - 1],
            };
        });
    };
}

type LinkProps = React.PropsWithChildren<{
    route: string;
    icon?: string;
    tooltip?: boolean;
    href?: string;
    name?: string;
    className?: string;
}>;

export const Link = React.forwardRef<HTMLLIElement, LinkProps>(function LinkInner(
    { children, route, icon, tooltip, href, ...props },
    ref
) {
    const context = useEContext();
    const tooltipProps = {};

    if (tooltip) {
        tooltipProps['aria-label'] = text(context.texts)(children, route);
        tooltipProps['data-microtip-position'] = 'bottom';
        tooltipProps['role'] = 'tooltip';
    }

    const content = icon ? (
        <React.Fragment>
            {icons[icon]}
            <span>
                <Text route={route}>{children}</Text>
            </span>
        </React.Fragment>
    ) : (
        <Text route={route}>{children}</Text>
    );

    return (
        <Authorize route={route}>
            {href && href.startsWith('http') ? (
                <li ref={ref}>
                    <a {...props} {...tooltipProps} href={href} target="_blank" rel="noopener noreferrer">
                        {content}
                    </a>
                </li>
            ) : (
                <li ref={ref}>
                    <NavLink {...props} {...tooltipProps} to={route} activeClassName="active">
                        {content}
                    </NavLink>
                </li>
            )}
        </Authorize>
    );
});

export default withEContext(Navigation);
